import { GameScene } from '@Core/GameScene/GameScene';
import { getSymbolRoles } from '@Core/Utils/SymbolUtils';
import { GameStore } from '@GameStore/GameStore';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { IMultiSymbolFeature } from '@GFTypes/index';
import Reel from './Reel';
import { ReelSpinningSimulation } from './ReelSpinningSimulation';
import ReelSymbol from './ReelSymbol';
import { ESymbolRoles } from './SymbolRoles';

export enum EAnimationStates {
    CREATE_STATE = 'created',
    ACC_STATE = 'accelerating',
    DEC_STATE = 'decelerating',
    BOUNCING_STATE = 'bouncing',
    END_STATE = 'ended'
}

export class ReelAnimation {
    public static readonly TENSION_SPINE_INTRO_ANIM = 'tension_intro';
    public static readonly TENSION_SPINE_LOOP_ANIM = 'tension_loop';

    private readonly scene: GameScene;
    private readonly gameStore: GameStore;
    private readonly container: Reel;
    private readonly reelIndex: number;
    private readonly symbolsPerReel: number;
    private readonly symbolsPerReelWithTopAndBottom: number;
    private finalSymbolIds: number[] = [];

    public simulation: ReelSpinningSimulation;

    constructor(
        container: Reel,
        scene: GameScene,
        reelIndex: number,
        private readonly multiSymbols?: IMultiSymbolFeature
    ) {
        this.scene = scene;
        this.container = container;
        this.gameStore = this.scene.game.gameStore;
        this.reelIndex = reelIndex;

        this.symbolsPerReel = this.gameStore.get(EGameStoreKeys.NumberOfSymbolsPerReel);
        this.symbolsPerReelWithTopAndBottom = this.symbolsPerReel + 2;
    }

    private getSymbols(): ReelSymbol[] {
        return this.container.children
            .filter((symbol): symbol is ReelSymbol => symbol instanceof ReelSymbol)
            .toSorted((symbolA, symbolB) => symbolA.y - symbolB.y);
    }

    public animate(): void {
        const bodyIds = [];
        for (const symbol of this.getSymbols()) {
            bodyIds.push(symbol.simulationBodyId);
        }

        if (this.simulation) this.simulation.destroy();
        this.simulation = new ReelSpinningSimulation(this.scene, this.reelIndex, this, bodyIds);
        this.simulation.animate();
    }

    public onUpdateFunction(delta: number) {
        if (!this.simulation || this.simulation.getAnimationState() === EAnimationStates.END_STATE) return;
        this.simulation.onUpdateFunction(delta);
        this.simulation.simulationBodies.forEach((body) => {
            body.update(delta / 1000);
        });
        this.syncSymbolsWithSimulation();
    }

    public syncSymbolsWithSimulation(): void {
        for (const body of this.simulation.sortedBodies()) {
            const { height } = this.scene.game.gameStore.get(EGameStoreKeys.SymbolParameters);
            const symbol = this.getSymbolBySimulationBodyId(body.id);
            symbol.y = body.positionY * height - 0.5 * height;
        }
    }

    public getSymbolBySimulationBodyId(id: number): ReelSymbol {
        return this.getSymbols().find((symbol) => symbol.simulationBodyId === id)!;
    }

    public switchToNextSymbol(bodyId: number, firstBodyId: number, final: boolean = false): void {
        const symbol = this.getSymbolBySimulationBodyId(bodyId);
        this.container.removeChild(symbol);
        this.container.addChildAt(symbol, 0);

        let newId = this.scene.game.reelSymbolsSetManager.getIdFromReelSet(this.reelIndex);
        let newSymbolIndex = 0;

        if (final) {
            newId = this.finalSymbolIds[this.simulation.finalSymbolsRevealed];
            newSymbolIndex = this.symbolsPerReelWithTopAndBottom - 1 - this.simulation.finalSymbolsRevealed;
        }

        const componentSymbols = this.multiSymbols?.componentSymbolIdsByMultiSymbolIds.get(newId);
        const isMultiSymbol = componentSymbols?.some((symbolId) => {
            const symbolRoles = getSymbolRoles(this.gameStore, symbolId);
            return symbolRoles.includes(ESymbolRoles.LOCK_AND_ROLL_DOUBLE_COIN);
        });

        const symbolSpineAsset = isMultiSymbol
            ? this.multiSymbols?.config.asset
            : this.gameStore.get(EGameStoreKeys.SymbolConfiguration).find((symbol) => symbol.id === newId)?.asset;

        if (symbolSpineAsset) {
            symbol.changeSymbol(newId, symbolSpineAsset, this.reelIndex, newSymbolIndex, this.scene.reels.symbolSpinePool, final);
        } else {
            throw new Error('SymbolData does not exist for provided id: ' + newId);
        }

        if (!final) symbol.switchToSpinningAnimation();
    }

    public passSpinResponse(finalSymbolIds: number[]): void {
        this.simulation.passSpinResponse();

        this.finalSymbolIds = finalSymbolIds.toReversed();
    }

    public quickStop(): void {
        this.simulation.quickStop();
    }
}
